import React, { useEffect, useState } from 'react';
import {
    Box, Button, Grid, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography,
    Accordion, AccordionSummary, AccordionDetails, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Autocomplete, Chip, Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Swal from 'sweetalert2';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';

function GestionPermisos() {
    const server = process.env.REACT_APP_API_ROUTE;
    const { user } = useUser();
    const { tienePermiso } = useAccesos();

    const [permisos, setPermisos] = useState([]);
    const [menus, setMenus] = useState([]);
    const [nuevoPermiso, setNuevoPermiso] = useState('');
    const [menuSeleccionado, setMenuSeleccionado] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [openDialog, setOpenDialog] = useState(false);

    useEffect(() => {
        obtenerPermisos();
        obtenerMenus();
    }, []);

    const obtenerPermisos = async () => {
        try {
            const response = await fetch(`${server}/SgPermisos`);
            const data = await response.json();
            setPermisos(data.$values || []);
        } catch (error) {
            console.error('Error al obtener los permisos:', error);
            Swal.fire('Error', 'No se pudieron cargar los permisos.', 'error');
        }
    };

    const obtenerMenus = async () => {
        try {
            const response = await fetch(`${server}/sg_menu`);
            const data = await response.json();
            setMenus(data.$values || []);
        } catch (error) {
            console.error('Error al obtener los menús:', error);
        }
    };

    const handleAgregarPermiso = async () => {
        if (!nuevoPermiso.trim()) {
            Swal.fire('Campo requerido', 'Debe ingresar un nombre de permiso.', 'warning');
            return;
        }
        if (!menuSeleccionado) {
            Swal.fire('Campo requerido', 'Debe seleccionar un menú asociado.', 'warning');
            return;
        }

        const body = {
            permiso: nuevoPermiso.trim(),
            id_menu: menuSeleccionado.id,
            ingresadoPor: user?.codigoUsuario || 'ADMIN'
        };

        try {
            const response = await fetch(`${server}/SgPermisos`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            if (response.ok) {
                Swal.fire('Éxito', 'Permiso agregado correctamente.', 'success');
                setNuevoPermiso('');
                setMenuSeleccionado(null);
                setOpenDialog(false);
                obtenerPermisos();
            } else {
                const errorData = await response.json();
                Swal.fire('Error', errorData.message || 'No se pudo guardar el permiso.', 'error');
            }
        } catch (error) {
            console.error('Error al guardar el permiso:', error);
            Swal.fire('Error', 'Hubo un problema al guardar el permiso.', 'error');
        }
    };

    const handleEliminarPermiso = async (idPermiso) => {
        const result = await Swal.fire({
            title: '¿Eliminar permiso?',
            text: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        try {
            const response = await fetch(`${server}/SgPermisos/${idPermiso}`, { method: 'DELETE' });
            if (response.ok) {
                Swal.fire('Eliminado', 'Permiso eliminado correctamente.', 'success');
                obtenerPermisos();
            } else {
                Swal.fire('Error', 'No se pudo eliminar el permiso.', 'error');
            }
        } catch (error) {
            Swal.fire('Error', 'Error al eliminar el permiso.', 'error');
        }
    };

    // Agrupar permisos por id_menu
    const permisosAgrupados = permisos.reduce((acc, permiso) => {
        const idMenu = permiso.id_menu || permiso.idMenu || 0;
        if (!acc[idMenu]) acc[idMenu] = [];
        acc[idMenu].push(permiso);
        return acc;
    }, {});

    const getMenuNombre = (idMenu) => {
        if (!idMenu || idMenu === 0) return 'Sin menú asignado';
        const menu = menus.find(m => m.id === idMenu);
        return menu ? menu.nombre_menu : `Menú #${idMenu}`;
    };

    // Filtrar menús que tienen permisos, por búsqueda
    const menuIdsConPermisos = Object.keys(permisosAgrupados)
        .map(Number)
        .filter(idMenu => {
            if (!searchQuery) return true;
            const nombreMenu = getMenuNombre(idMenu).toLowerCase();
            const tienePermisoMatch = permisosAgrupados[idMenu]?.some(p =>
                p.permiso?.toLowerCase().includes(searchQuery.toLowerCase())
            );
            return nombreMenu.includes(searchQuery.toLowerCase()) || tienePermisoMatch;
        })
        .sort((a, b) => {
            const nameA = getMenuNombre(a);
            const nameB = getMenuNombre(b);
            return nameA.localeCompare(nameB);
        });

    return (
        <div className="right_col" role="main">
            <div className="x_content">
                <Box sx={{ padding: 4 }}>
                    <Typography variant="h5" gutterBottom>
                        Gestión de Permisos Especiales por Menú
                    </Typography>

                    <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                        <Grid item xs={8} md={6}>
                            <TextField
                                fullWidth
                                label="Buscar permiso o menú..."
                                variant="outlined"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={4} md={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => setOpenDialog(true)}
                                fullWidth
                                disabled={!tienePermiso('Crear Permiso')}
                            >
                                Nuevo Permiso
                            </Button>
                        </Grid>
                    </Grid>

                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Total: {permisos.length} permisos en {menuIdsConPermisos.length} menús
                    </Typography>

                    {menuIdsConPermisos.map(idMenu => (
                        <Accordion key={idMenu} defaultExpanded={false}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {getMenuNombre(idMenu)}
                                    </Typography>
                                    <Chip
                                        label={`${permisosAgrupados[idMenu]?.length || 0} permisos`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>#</TableCell>
                                                <TableCell>Permiso</TableCell>
                                                <TableCell>Ingresado Por</TableCell>
                                                <TableCell align="center">Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(permisosAgrupados[idMenu] || []).map((permiso, index) => (
                                                <TableRow key={permiso.id_permiso || permiso.idPermiso || index}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>{permiso.permiso}</TableCell>
                                                    <TableCell>{permiso.ingresadoPor || permiso.ingresado_por}</TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="Eliminar permiso">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleEliminarPermiso(permiso.id_permiso || permiso.idPermiso)}
                                                                disabled={!tienePermiso('Eliminar Permiso')}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        </Accordion>
                    ))}

                    {/* Dialog para agregar nuevo permiso */}
                    <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>Agregar Nuevo Permiso</DialogTitle>
                        <DialogContent>
                            <Autocomplete
                                options={menus}
                                getOptionLabel={(option) => option.nombre_menu || ''}
                                value={menuSeleccionado}
                                onChange={(e, newValue) => setMenuSeleccionado(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Menú asociado" variant="outlined" fullWidth margin="dense" />
                                )}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                sx={{ mt: 1 }}
                            />
                            <TextField
                                fullWidth
                                label="Nombre del Permiso"
                                variant="outlined"
                                value={nuevoPermiso}
                                onChange={(e) => setNuevoPermiso(e.target.value)}
                                margin="dense"
                                placeholder="Ej: Crear Cliente, Editar Orden de Trabajo..."
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                            <Button
                                onClick={handleAgregarPermiso}
                                variant="contained"
                                color="primary"
                            >
                                Agregar
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            </div>
        </div>
    );
}

export default GestionPermisos;
